// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ShippingCarrier,
  getShippingCarrierLabel,
} from '@/constants/shipping-carrier';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  TextField,
} from '@mui/material';
import { ForwardedRef, forwardRef } from 'react';

/**
 * Determines if the option is equal to the value.
 *
 * @param option - A carrier option to compare.
 * @param value - A carrier value to compare.
 */
function isOptionEqualToValue(option: ShippingCarrier, value: ShippingCarrier) {
  return option === value;
}

/**
 * Returns the key of the option.
 *
 * @param option - A carrier option for which to get the key.
 */
function getOptionKey(option: ShippingCarrier) {
  return option;
}

interface Props<Option extends ShippingCarrier>
  extends Omit<
    AutocompleteProps<Option, false, false, false>,
    'renderInput' | 'isOptionEqualToValue' | 'getOptionLabel'
  > {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * If provided, the placeholder text will be displayed in the input element.
   */
  placeholder?: string;
}

/**
 * Renders a carrier autocomplete component and forwards a ref to the input
 * element.
 */
function ShippingCarrierAutocompleteRenderer<Option extends ShippingCarrier>(
  props: Props<Option>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { name, label, placeholder, error, helperText, ...autocompleteProps } =
    props;

  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...parameters}
      />
    );
  };

  return (
    <Autocomplete
      {...autocompleteProps}
      renderInput={renderInput}
      getOptionKey={getOptionKey}
      getOptionLabel={getShippingCarrierLabel}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
}

/**
 * An autocomplete component for selecting a carrier from a list of options.
 */
export const ShippingCarrierAutocomplete = forwardRef(
  ShippingCarrierAutocompleteRenderer
);
